/**
 * Returns `count` placeholder objects typed as `T`, intended for iterating alongside real
 * data through a single `@for` loop with `[sgSkeleton]="loading"` on each cell.
 *
 * The placeholders are empty objects (`{} as T`). The cast is sound when used with
 * `[sgSkeleton]="loading"` because the directive overlays a shimmer over each cell during
 * loading — the bound data fields aren't visually displayed even though the bindings still
 * evaluate. For DTOs with all-optional fields (the codegen default in this repo) `{}` is
 * structurally assignable; for DTOs with required fields, pass a factory that returns a
 * fully-shaped placeholder.
 *
 * @example
 * ```ts
 * readonly placeholderRows = skeletonRows<TopSecurityByRevenueDto>(3);
 * ```
 *
 * ```html
 * @for (row of state.loading ? placeholderRows : (state.vm?.rows ?? []); track ...) {
 *   <sg-text [sgSkeleton]="state.loading">{{ row.compositeTicker }}</sg-text>
 * }
 * ```
 *
 * @example With a factory for non-optional fields:
 * ```ts
 * readonly placeholderRows = skeletonRows<RequiredFieldDto>(5, () => ({ id: 0, name: '' }));
 * ```
 */
export const skeletonRows = <T>(count: number, factory: () => T = () => ({}) as T): T[] =>
  Array.from({ length: count }, factory);
